import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Settings, HelpCircle, Zap, ArrowRight } from 'lucide-react';
import { HelpTooltip } from '../shared';
import { SystemPromptGuidance } from './SystemPromptGuidance';
import { ChatbotConfig } from '@/services/chatbotService';
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { useTranslation } from 'react-i18next';

interface AIConfigurationProps {
  config: ChatbotConfig;
  onUpdate: (updates: Partial<ChatbotConfig>) => void;
}

export function AIConfiguration({ config, onUpdate }: AIConfigurationProps) {
  const { t } = useTranslation('business', { keyPrefix: 'communication' });
  const [showSystemPromptGuide, setShowSystemPromptGuide] = useState(false);

  const handleSelectExample = (prompt: string) => {
    onUpdate({ systemPrompt: prompt });
    setShowSystemPromptGuide(false);
    toast({ title: t('templateApplied') });
  };

  return (
    <Card className="border border-slate-200/60 shadow-lg shadow-slate-900/8 bg-white hover:shadow-xl hover:shadow-slate-900/12 transition-shadow duration-300">
      <CardHeader className="pb-4 border-b border-slate-100/80">
        <CardTitle className="flex items-center gap-2 text-lg">
          <div className="w-8 h-8 rounded-lg bg-emerald-100 border border-emerald-200/50 flex items-center justify-center shadow-sm">
            <Settings className="w-4 h-4 text-emerald-600" />
          </div>
          {t('aiKnowledgePersonality')}
          <HelpTooltip content={t('aiKnowledgeDescription')}>
            <HelpCircle className="w-4 h-4 text-slate-400 cursor-help" />
          </HelpTooltip>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6 pt-6">
        <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-emerald-50/80 to-emerald-50/70 border border-emerald-200/60 rounded-lg shadow-sm">
          <Zap className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-emerald-900 mb-1">{t('thisIsCrucial')}</h4>
            <p className="text-sm text-emerald-800 mb-2">
              {t('systemPromptDescription')}
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowSystemPromptGuide(!showSystemPromptGuide)}
              className="text-emerald-700 border-emerald-300/80 hover:bg-emerald-100 shadow-sm"
            >
              {showSystemPromptGuide ? t('hideExamplesTemplates') : t('showExamplesTemplates')}
              <ArrowRight className={cn("w-3 h-3 ml-1 transition-transform", showSystemPromptGuide && "rotate-90")} />
            </Button>
          </div>
        </div>

        {showSystemPromptGuide && (
          <div className="border border-slate-200/60 rounded-lg p-4 bg-slate-50/50 shadow-sm">
            <SystemPromptGuidance onSelectExample={handleSelectExample} />
          </div>
        )}
        
        <div className="space-y-2">
          <Label htmlFor="systemPrompt" className="flex items-center gap-2">
            {t('systemInstructions')}
            <Badge variant="secondary" className="bg-orange-100 text-orange-800 border border-orange-200/50 shadow-sm">
              {t('systemInstructionsRequired')}
            </Badge>
          </Label>
          <Textarea
            id="systemPrompt"
            value={config.systemPrompt}
            onChange={e => onUpdate({ systemPrompt: e.target.value })}
            placeholder={t('systemPromptPlaceholder')}
            rows={6}
            className="focus:ring-emerald-500 focus:border-emerald-500 resize-none border-slate-200/80 shadow-sm"
          />
          <div className="flex items-center justify-between text-xs">
            <p className="text-slate-600">
              {t('systemPromptHelp')}
            </p>
            <span className={cn(
              "font-medium px-2 py-1 rounded-md",
              config.systemPrompt?.length < 50 && "text-orange-600 bg-orange-50",
              config.systemPrompt?.length >= 50 && config.systemPrompt?.length < 100 && "text-yellow-600 bg-yellow-50",
              config.systemPrompt?.length >= 100 && "text-emerald-600 bg-emerald-50"
            )}>
              {config.systemPrompt?.length || 0} {t('characters')}
              {config.systemPrompt?.length < 50 && ` (${t('addMoreDetail')})`}
              {config.systemPrompt?.length >= 50 && config.systemPrompt?.length < 100 && ` (${t('goodStart')})`}
              {config.systemPrompt?.length >= 100 && ` (${t('excellent')})`}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
} 